import React from 'react';

import errorCodeList from '@client/config/errorCodeList';
import { SpectrogramPoint } from '@ledium/types';
import { Alert, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import WavelengthChart from './WavelengthChart';

interface AutocompleteOption {
    name: string;
    description?: string;
    errorCode?: string;
    optionChart?: SpectrogramPoint[];
    additionalItemListInfo?: React.ReactNode;
    lockedReason?: string;
    linkedReason?: string;
}

interface AutocompleteOptionWithChartProps {
    option: AutocompleteOption;
    errorCodes?: string;
    spectrogramData?: SpectrogramPoint[] | null;
    size?: 'large' | 'small';
    onDoubleClick?: React.MouseEventHandler;
}

const useStyles = makeStyles({
    optionCellChartLarge: {
        maxWidth: '200px'
    },
    optionCellChartSmall: {
        maxWidth: '120px'
    },
    message: {
        marginTop: '10px'
    }
});

const AutocompleteOptionWithChart = ({ option, errorCodes, spectrogramData = null, size = 'large', onDoubleClick }: AutocompleteOptionWithChartProps) => {
    const classes = useStyles();
    let error = '';
    const spectrogram = spectrogramData || option.optionChart || null;

    // reason: errorCodeList is keyed by page name at runtime; dynamic indexing not in the static type
    const errorCodeMap = errorCodeList as unknown as Record<string, Record<string, string> | undefined>;
    if (errorCodes && option.errorCode && errorCodeMap[errorCodes] && errorCodeMap[errorCodes][option.errorCode]) {
        error = errorCodeMap[errorCodes][option.errorCode];
    }

    return (
        <Grid container spacing={2} justifyContent="space-between" onDoubleClick={onDoubleClick}>
            {spectrogram ? (
                <Grid item xs={false} sm className={size === 'large' ? classes.optionCellChartLarge : classes.optionCellChartSmall}>
                    <WavelengthChart data={spectrogram} small={true} />
                </Grid>
            ) : null}
            <Grid item xs={12} sm>
                <Typography gutterBottom variant={size === 'large' ? 'h6' : 'subtitle2'}>
                    {option.name}
                </Typography>
                <Typography variant="body1">{option.description}</Typography>
                {option.additionalItemListInfo ? <Typography variant="body2">{option.additionalItemListInfo}</Typography> : null}
                {error || option.lockedReason || option.linkedReason ? (
                    <div className={classes.message}>
                        {error ? <Alert severity="error">{error}</Alert> : null}
                        {option.lockedReason ? <Alert severity="warning">{option.lockedReason}</Alert> : null}
                        {!option.lockedReason && option.linkedReason ? <Alert severity="info">{option.linkedReason}</Alert> : null}
                    </div>
                ) : null}
            </Grid>
        </Grid>
    );
};

export default React.memo(AutocompleteOptionWithChart);
